'use client'

import * as React from 'react'

import { cn } from '../cn'

/**
 * Standard app-shell layout for Ordo apps.
 *
 * Fixed viewport height, overflow-hidden outer + column. Sidebar sits on the
 * left as a flex child; header + main sit in the right column. Only <main>
 * scrolls, so the topbar and sidebar stay anchored to the viewport.
 *
 * AppShell ALSO owns the mobile sidebar drawer state. Any descendant
 * `AppTopBarHamburger` and `AppSidebarDrawer` will auto-bind to the same
 * `open`/`setOpen` pair via `SidebarStateContext`, so apps don't have to
 * thread `useState` and `onOpenChange` through their layouts. This was the
 * #1 footgun in the previous design — every app re-discovered that the
 * hamburger does nothing without manual wiring.
 *
 * Apps can still pass `open`/`onClick` explicitly to override.
 *
 * Layouts:
 *  - `classic` (default): `[Sidebar | Content(TopBar / Main)]`. Every app that
 *    doesn't opt in renders exactly this — apps/ordo depends on it.
 *  - `unified`: the internal-tools shell. A full-width `AppTopBar` on top
 *    (desktop only), then `<AppShellBody>` holding the sidebar and content.
 *    On phones the top bar hides and `MobileTopRow` + `MobileFloatingActions`
 *    take over. Opt in per app with `layout="unified"`.
 */

interface SidebarState {
  open: boolean
  setOpen: (open: boolean) => void
}

const SidebarStateContext = React.createContext<SidebarState | null>(null)

/**
 * Read the AppShell-managed sidebar state. Returns null when called outside
 * an `<AppShell>` so primitives can degrade to props-driven behavior. Apps
 * shouldn't need this directly — the hamburger and drawer auto-bind.
 */
export function useSidebarState(): SidebarState | null {
  return React.useContext(SidebarStateContext)
}

export type AppShellLayout = 'classic' | 'unified'

/**
 * How the phone menu opens. `drawer` slides the sidebar over the page in a
 * sheet; it is the default for the `classic` layout (apps/ordo). `reveal`, the
 * default for every `unified` internal app, slides the whole app surface to the right with
 * a shadow on its edge and shows the menu underneath it (PROD-1530).
 */
export type AppShellMobileNav = 'drawer' | 'reveal'

/** The Tailwind `md` breakpoint, where the desktop sidebar takes over from the phone menu. */
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)'

/** How long the reveal menu's slide takes. The menu stays painted this long after closing. */
export const SHELL_REVEAL_DURATION_MS = 300

/** A media query list as far as `closeWhenDesktop` needs it. `MediaQueryList` satisfies it. */
export interface BreakpointQuery {
  addEventListener(type: 'change', listener: (event: { matches: boolean }) => void): void
  removeEventListener(type: 'change', listener: (event: { matches: boolean }) => void): void
}

/**
 * Calls `close` whenever the viewport grows into the desktop layout, and
 * returns the unsubscribe. The phone menu is a modal sheet; left open across
 * the breakpoint it sat over the desktop sidebar at the wrong width, with its
 * backdrop and scroll lock still applied, and closing it revealed the second
 * sidebar underneath (PROD-1525).
 */
export function closeWhenDesktop(query: BreakpointQuery, close: () => void): () => void {
  const onChange = (event: { matches: boolean }) => {
    if (event.matches) {
      close()
    }
  }
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

/**
 * Root classes for the `reveal` phone menu. `open` slides the app right;
 * `revealed` (open, or still sliding back after closing) lifts the app above
 * the menu layer so the menu never paints over it mid-animation. Desktop
 * never moves: the menu is closed there by `closeWhenDesktop`, and the `md:`
 * resets cover the frame before that lands.
 */
export function revealShellClasses({ open, revealed }: { open: boolean; revealed: boolean }): string {
  return cn(
    'transition-[translate,box-shadow,border-radius] duration-300 ease-out motion-reduce:transition-none',
    revealed && 'relative z-10',
    // A shadow on the left edge only. In a browser tab the page stops at the
    // browser's own bars, so the panel's top and bottom corners show there:
    // a slight rounding on the left corners keeps them from looking cut off.
    // In the installed app the panel runs past the screen edges, so it stays
    // square there. (A large radius on every corner looked wrong at the bars.)
    open &&
      'translate-x-[min(82vw,320px)] shadow-[-16px_0_40px_-12px_rgb(0_0_0/0.25)] [@media(display-mode:browser)]:rounded-l-[12px] md:translate-x-0 md:shadow-none md:rounded-none',
  )
}

/**
 * Where the app surface sits while a finger drags the reveal menu closed:
 * the open width plus the (leftward, negative) drag, kept between closed (0)
 * and fully open.
 */
export function revealDragOffset(width: number, dx: number): number {
  return Math.min(width, Math.max(0, width + dx))
}

/**
 * Whether letting go of a drag closes the reveal menu: past a third of the
 * way, or a quick flick to the left (velocity in px/ms, negative is left).
 */
export function revealDragCloses({
  offset,
  width,
  velocity,
}: {
  offset: number
  width: number
  velocity: number
}): boolean {
  return offset < (width * 2) / 3 || velocity < -0.5
}

/**
 * Stop the document scrolling in either direction while the reveal menu is
 * open. The slid-aside app sticks out past the right edge of the screen, and
 * without this the browser let a sideways swipe pan the whole page to it.
 * Returns the restore.
 */
function lockDocumentScroll(doc: Document): () => void {
  const targets = [doc.documentElement, doc.body]
  const previous = targets.map((el) => ({ overflow: el.style.overflow, overscroll: el.style.overscrollBehavior }))
  for (const el of targets) {
    el.style.overflow = 'hidden'
    el.style.overscrollBehavior = 'none'
  }
  return () => {
    targets.forEach((el, i) => {
      el.style.overflow = previous[i]?.overflow ?? ''
      el.style.overscrollBehavior = previous[i]?.overscroll ?? ''
    })
  }
}

/** A page's primary "create" action, shown as the blue + above the mobile search circle. */
export interface ShellCreateAction {
  label: string
  onCreate: () => void
}

interface ShellChrome {
  layout: AppShellLayout
  mobileNav: AppShellMobileNav
  /** Reveal mode: the menu is open or still sliding closed, so its layer must paint. */
  navRevealed: boolean
  /** Reveal mode: the app's position while a finger drags the menu closed; null otherwise. */
  setNavDrag: (offset: number | null) => void
  createAction: ShellCreateAction | null
  setCreateAction: (action: ShellCreateAction | null) => void
  /** True while the mobile app grid is open; floating actions tuck away. */
  appGridOpen: boolean
  setAppGridOpen: (open: boolean) => void
}

const noop = () => undefined

const ShellChromeContext = React.createContext<ShellChrome>({
  layout: 'classic',
  mobileNav: 'drawer',
  navRevealed: false,
  setNavDrag: noop,
  createAction: null,
  setCreateAction: noop,
  appGridOpen: false,
  setAppGridOpen: noop,
})

/** The layout of the nearest `<AppShell>`; `classic` outside one. */
export function useShellLayout(): AppShellLayout {
  return React.useContext(ShellChromeContext).layout
}

/** Shared state for the unified shell's mobile chrome. */
export function useShellChrome(): ShellChrome {
  return React.useContext(ShellChromeContext)
}

/**
 * Register the current page's create action with the shell. Pass `null` when
 * the page has nothing to create (or the user can't). Cleared on unmount, so
 * navigating away removes the +.
 */
export function useShellCreateAction(action: ShellCreateAction | null): void {
  const { setCreateAction } = useShellChrome()
  const onCreateRef = React.useRef(action?.onCreate)
  onCreateRef.current = action?.onCreate
  const enabled = action !== null
  const label = action?.label ?? ''
  React.useEffect(() => {
    if (!enabled) {
      return
    }
    setCreateAction({ label, onCreate: () => onCreateRef.current?.() })
    return () => setCreateAction(null)
  }, [enabled, label, setCreateAction])
}

export interface AppShellProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * When provided, the mobile sidebar drawer auto-closes whenever this
   * value changes. Pass `useLocation().pathname` from react-router so the
   * drawer dismisses on navigation. Optional to keep `@ordo/ui` free of
   * router deps — without it the drawer stays open until the user closes
   * it explicitly.
   */
  pathname?: string
  /** `classic` (default) or the opt-in `unified` internal-tools shell. */
  layout?: AppShellLayout
  /** How `AppSidebarDrawer` opens on phones. Defaults to `reveal` for `unified`, `drawer` for `classic`. */
  mobileNav?: AppShellMobileNav
}

export function AppShell({
  pathname,
  layout = 'classic',
  mobileNav: mobileNavProp,
  children,
  className,
  style,
  ...props
}: AppShellProps) {
  // Every internal app gets the reveal menu (Hunter, 2026-10-17); the
  // customer-facing classic shell keeps its sheet drawer.
  const mobileNav = mobileNavProp ?? (layout === 'unified' ? 'reveal' : 'drawer')
  const [open, setOpen] = React.useState(false)
  const [createAction, setCreateAction] = React.useState<ShellCreateAction | null>(null)
  const [appGridOpen, setAppGridOpen] = React.useState(false)
  // Reveal mode keeps the menu layer painted until the app has slid back over it.
  const [closing, setClosing] = React.useState(false)
  const [navDrag, setNavDrag] = React.useState<number | null>(null)
  const reveal = mobileNav === 'reveal'
  const navRevealed = reveal && (open || closing)
  // A finger dragging the menu closed moves the app with it, untransitioned.
  const dragging = reveal && open && navDrag !== null

  React.useEffect(() => {
    if (!navRevealed || typeof document === 'undefined') {
      return
    }
    return lockDocumentScroll(document)
  }, [navRevealed])

  // The phone menu and app grid are phone-only. Close them when the window
  // grows into the desktop layout (PROD-1525).
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return
    }
    return closeWhenDesktop(window.matchMedia(DESKTOP_MEDIA_QUERY), () => {
      setOpen(false)
      setAppGridOpen(false)
    })
  }, [])

  React.useEffect(() => {
    if (!reveal) {
      return
    }
    if (open) {
      setClosing(true)
      return
    }
    const timer = setTimeout(() => setClosing(false), SHELL_REVEAL_DURATION_MS)
    return () => clearTimeout(timer)
  }, [open, reveal])

  // Close on route change. Tasker and Factory both want this; doing it once
  // here means apps don't need a useEffect of their own.
  React.useEffect(() => {
    if (pathname !== undefined) {
      setOpen(false)
      setAppGridOpen(false)
    }
  }, [pathname])

  const value = React.useMemo<SidebarState>(() => ({ open, setOpen }), [open])
  const chrome = React.useMemo<ShellChrome>(
    () => ({ layout, mobileNav, navRevealed, setNavDrag, createAction, setCreateAction, appGridOpen, setAppGridOpen }),
    [layout, mobileNav, navRevealed, createAction, appGridOpen],
  )

  return (
    <SidebarStateContext.Provider value={value}>
      <ShellChromeContext.Provider value={chrome}>
        <div
          className={cn(
            layout === 'unified'
              ? 'flex h-dvh flex-col overflow-hidden bg-background'
              : 'flex h-dvh overflow-hidden bg-background',
            reveal && revealShellClasses({ open, revealed: navRevealed }),
            className,
          )}
          // While the reveal menu is open the app surface is inert: the menu
          // owns focus, and a tap on the exposed edge closes it.
          inert={reveal && open ? true : undefined}
          style={dragging ? { ...style, translate: `${navDrag}px 0`, transition: 'none' } : style}
          {...props}>
          {children}
        </div>
      </ShellChromeContext.Provider>
    </SidebarStateContext.Provider>
  )
}

/**
 * Unified layout only: the row under the full-width top bar that holds the
 * sidebar and `<AppShellContent>`.
 */
export function AppShellBody({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex min-h-0 flex-1 overflow-hidden', className)} {...props}>
      {children}
    </div>
  )
}

/**
 * Right-hand column: header + scrollable main.
 * Place the app's topbar and <AppShellMain> inside this.
 */
export function AppShellContent({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-1 flex-col overflow-hidden', className)} {...props}>
      {children}
    </div>
  )
}

/**
 * Scrollable content area. Render PageContainer inside for standard padding.
 * In the unified layout it also leaves room on phones for the floating top
 * row and the bottom-right circles, which sit over the content.
 */
export const AppShellMain = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ children, className, ...props }, ref) => {
    const layout = useShellLayout()
    return (
      <main
        ref={ref}
        className={cn(
          'flex-1 overflow-y-auto',
          layout === 'unified' &&
            'pt-[calc(env(safe-area-inset-top,0px)+5.25rem)] pb-[calc(env(safe-area-inset-bottom,0px)+6rem)] md:pt-0 md:pb-0',
          className,
        )}
        {...props}>
        {children}
      </main>
    )
  },
)
AppShellMain.displayName = 'AppShellMain'
